import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";

import { getSocket } from "../../services/socket";
import api from "../../services/api";

import toastError from "../../errors/toastError";
import { AuthContext } from "../Auth/AuthContext";

const StaticResourcesContext = createContext();

const StaticResourcesContextProvider = ({ children }) => {
  // 🔧 CONSTANTES PARA CACHE DE RECURSOS ESTÁTICOS
  const QUEUES_CACHE_DURATION = 60 * 60 * 1000; // 1 hora - las colas no cambian frecuentemente
  const QUICK_MESSAGES_CACHE_DURATION = 30 * 60 * 1000; // 30 minutos - pueden editarse más seguido
  const USER_CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 horas - datos de usuario cambian rara vez
  const TICKETS_BASIC_CACHE_DURATION = 10 * 60 * 1000; // 10 minutos - info básica de tickets

  const CACHE_KEYS = {
    QUEUES: 'venttify_static_queues_',
    QUICK_MESSAGES: 'venttify_static_quick_messages_', 
    USER: 'venttify_static_user_',
    TICKETS_BASIC: 'venttify_static_tickets_basic_'
  };

  const EXPIRY_KEYS = {
    QUEUES: 'venttify_static_queues_expiry_',
    QUICK_MESSAGES: 'venttify_static_quick_messages_expiry_',
    USER: 'venttify_static_user_expiry_',
    TICKETS_BASIC: 'venttify_static_tickets_basic_expiry_'
  };

  // Estados para cache en memoria
  const [queuesCache, setQueuesCache] = useState(null);
  const [quickMessagesCache, setQuickMessagesCache] = useState({});
  const [userCache, setUserCache] = useState({});
  const [ticketsBasicCache, setTicketsBasicCache] = useState({});
  const [loadingStates, setLoadingStates] = useState({
    queues: false,
    quickMessages: false,
    user: false,
    tickets: {}
  });
  const queuesRequestRef = useRef(null);

  const { user } = useContext(AuthContext);
  const companyId = user?.companyId || localStorage.getItem("companyId");

  // 📦 FUNCIONES GENÉRICAS DE CACHE CON LOCALSTORAGE
  
  const saveToLocalStorage = (type, key, data, duration) => {
    try {
      const cacheData = {
        data,
        cachedAt: Date.now()
      };
      
      localStorage.setItem(`${CACHE_KEYS[type]}${key}`, JSON.stringify(cacheData));
      localStorage.setItem(`${EXPIRY_KEYS[type]}${key}`, (Date.now() + duration).toString());
      
      console.log(`💾 ${type} cache guardado en localStorage para ${key}`);
    } catch (error) {
      console.warn(`Error guardando ${type} cache en localStorage:`, error);
      // Limpiar localStorage si está lleno
      try {
        Object.keys(localStorage).forEach(storageKey => {
          if (storageKey.startsWith(CACHE_KEYS[type]) || storageKey.startsWith(EXPIRY_KEYS[type])) {
            localStorage.removeItem(storageKey);
          }
        });
      } catch (cleanupError) {
        console.warn(`Error limpiando ${type} cache de localStorage:`, cleanupError);
      }
    }
  };

  const getFromLocalStorage = (type, key) => {
    try {
      const expiryStr = localStorage.getItem(`${EXPIRY_KEYS[type]}${key}`);
      if (!expiryStr) return null;

      const expiry = parseInt(expiryStr);
      if (expiry <= Date.now()) {
        // Cache expirado, limpiar
        localStorage.removeItem(`${CACHE_KEYS[type]}${key}`);
        localStorage.removeItem(`${EXPIRY_KEYS[type]}${key}`);
        console.log(`🗑️ ${type} cache expirado para ${key}, limpiando localStorage`);
        return null;
      }

      const cacheStr = localStorage.getItem(`${CACHE_KEYS[type]}${key}`);
      if (!cacheStr) return null;

      const cacheData = JSON.parse(cacheStr);
      console.log(`📦 ${type} cache recuperado de localStorage para ${key}`);
      return cacheData.data;
    } catch (error) {
      console.warn(`Error leyendo ${type} cache de localStorage:`, error);
      // Limpiar cache corrupto
      try {
        localStorage.removeItem(`${CACHE_KEYS[type]}${key}`);
        localStorage.removeItem(`${EXPIRY_KEYS[type]}${key}`);
      } catch (cleanupError) {
        console.warn(`Error limpiando ${type} cache corrupto:`, cleanupError);
      }
      return null;
    }
  };

  const isLocalStorageCacheValid = (type, key) => {
    try {
      const expiryStr = localStorage.getItem(`${EXPIRY_KEYS[type]}${key}`);
      if (!expiryStr) return false;
      
      const expiry = parseInt(expiryStr);
      return expiry > Date.now();
    } catch (error) {
      console.warn(`Error verificando validez de ${type} cache:`, error);
      return false;
    }
  };

  // 🎯 FUNCIONES ESPECÍFICAS PARA CADA RECURSO

  const fetchQueues = useCallback(async (forceUpdate = false) => {
    if (!companyId) return [];

    const cacheKey = companyId;

    if (!forceUpdate && queuesCache && isLocalStorageCacheValid('QUEUES', cacheKey)) {
      return queuesCache;
    }

    if (!forceUpdate) {
      const localData = getFromLocalStorage('QUEUES', cacheKey);
      if (Array.isArray(localData)) {
        setQueuesCache(localData);
        return localData;
      }
    }

    if (queuesRequestRef.current) {
      return queuesRequestRef.current;
    }

    queuesRequestRef.current = api.get('/queue', { params: { companyId } })
      .then(({ data }) => {
        const queues = Array.isArray(data)
          ? data
          : Array.isArray(data?.queues)
            ? data.queues
            : [];

        setQueuesCache(queues);
        saveToLocalStorage('QUEUES', cacheKey, queues, QUEUES_CACHE_DURATION);
        return queues;
      })
      .catch((err) => {
        console.error('Error cargando colas:', err);
        toastError('Error al cargar las colas');
        return [];
      })
      .finally(() => {
        queuesRequestRef.current = null;
        setLoadingStates(prev => ({ ...prev, queues: false }));
      });

    setLoadingStates(prev => ({ ...prev, queues: true }));
    return queuesRequestRef.current;
  }, [companyId, queuesCache]);

  // ✅ CACHE DE MENSAJES RÁPIDOS
  const fetchQuickMessages = useCallback(async (userId = null, forceUpdate = false) => {
    // Usar prioritariamente el contexto de autenticación, luego localStorage como fallback
    const currentUserId = userId || user?.id || localStorage.getItem("userId");
    const cacheKey = `${companyId}_${currentUserId}`;
    
    try {
      // Verificar cache en memoria primero
      if (!forceUpdate && quickMessagesCache[cacheKey] && isLocalStorageCacheValid('QUICK_MESSAGES', cacheKey)) {
        console.log(`✅ Usando cache de mensajes rápidos para usuario ${currentUserId}`);
        return quickMessagesCache[cacheKey];
      }

      // Verificar localStorage si no hay en memoria
      if (!forceUpdate) {
        const localData = getFromLocalStorage('QUICK_MESSAGES', cacheKey);
        if (localData) {
          setQuickMessagesCache(prev => ({ ...prev, [cacheKey]: localData }));
          console.log(`🔄 Sincronizando cache de mensajes rápidos desde localStorage`);
          return localData;
        }
      }

      console.log(`🌐 Fetcheando mensajes rápidos desde servidor para usuario ${currentUserId}`);
      setLoadingStates(prev => ({ ...prev, quickMessages: true }));
      
      const { data } = await api.get('/quick-messages/list', {
        params: { companyId, userId: currentUserId }
      });

      // Actualizar cache
      setQuickMessagesCache(prev => ({ ...prev, [cacheKey]: data }));
      saveToLocalStorage('QUICK_MESSAGES', cacheKey, data, QUICK_MESSAGES_CACHE_DURATION);

      console.log(`✅ Mensajes rápidos cargados y cacheados: ${data?.length || 0} mensajes`);
      return data;
    } catch (err) {
      console.error('Error cargando mensajes rápidos:', err);
      toastError("Error al cargar los mensajes rápidos");
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, quickMessages: false }));
    }
  }, [companyId, user?.id, quickMessagesCache]);

  // ✅ CACHE DE DATOS DE USUARIO
  const fetchUserData = useCallback(async (userId = null, forceUpdate = false) => {
    // Usar prioritariamente el contexto de autenticación, luego localStorage como fallback
    const currentUserId = userId || user?.id || localStorage.getItem("userId");
    const cacheKey = currentUserId;
    
    try {
      // Verificar cache en memoria primero
      if (!forceUpdate && userCache[cacheKey] && isLocalStorageCacheValid('USER', cacheKey)) {
        console.log(`✅ Usando cache de datos de usuario ${currentUserId}`);
        return userCache[cacheKey];
      }

      // Verificar localStorage si no hay en memoria
      if (!forceUpdate) {
        const localData = getFromLocalStorage('USER', cacheKey);
        if (localData) {
          setUserCache(prev => ({ ...prev, [cacheKey]: localData }));
          console.log(`🔄 Sincronizando cache de usuario desde localStorage`);
          return localData;
        }
      }

      console.log(`🌐 Fetcheando datos de usuario desde servidor para usuario ${currentUserId}`);
      setLoadingStates(prev => ({ ...prev, user: true }));
      
      const { data } = await api.get(`/users/${currentUserId}`);

      // Actualizar cache
      setUserCache(prev => ({ ...prev, [cacheKey]: data }));
      saveToLocalStorage('USER', cacheKey, data, USER_CACHE_DURATION);

      console.log(`✅ Datos de usuario cargados y cacheados para ${data?.name || 'usuario'}`);
      return data;
    } catch (err) {
      console.error('Error cargando datos de usuario:', err);
      // En caso de error, retornar el usuario del contexto como fallback
      return user;
    } finally {
      setLoadingStates(prev => ({ ...prev, user: false }));
    }
  }, [user?.id, userCache]);

  // ✅ CACHE DE INFORMACIÓN BÁSICA DE TICKETS
  const fetchTicketBasicInfo = useCallback(async (ticketId, forceUpdate = false) => {
    const cacheKey = ticketId;
    
    try {
      // Verificar cache en memoria primero
      if (!forceUpdate && ticketsBasicCache[cacheKey] && isLocalStorageCacheValid('TICKETS_BASIC', cacheKey)) {
        console.log(`✅ Usando cache de info básica de ticket ${ticketId}`);
        return ticketsBasicCache[cacheKey];
      }

      // Verificar localStorage si no hay en memoria
      if (!forceUpdate) {
        const localData = getFromLocalStorage('TICKETS_BASIC', cacheKey);
        if (localData) {
          setTicketsBasicCache(prev => ({ ...prev, [cacheKey]: localData }));
          console.log(`🔄 Sincronizando cache de ticket básico desde localStorage`);
          return localData;
        }
      }

      console.log(`🌐 Fetcheando info básica de ticket desde servidor para ticket ${ticketId}`);
      setLoadingStates(prev => ({ 
        ...prev, 
        tickets: { ...prev.tickets, [ticketId]: true }
      }));
      
      const { data } = await api.get(`/tickets/${ticketId}`);

      // Actualizar cache
      setTicketsBasicCache(prev => ({ ...prev, [cacheKey]: data }));
      saveToLocalStorage('TICKETS_BASIC', cacheKey, data, TICKETS_BASIC_CACHE_DURATION);

      console.log(`✅ Info básica de ticket cargada y cacheada para ticket ${ticketId}`);
      return data;
    } catch (err) {
      console.error('Error cargando info básica de ticket:', err);
      toastError("Error al cargar información del ticket");
      return null;
    } finally {
      setLoadingStates(prev => ({ 
        ...prev, 
        tickets: { ...prev.tickets, [ticketId]: false }
      }));
    }
  }, [ticketsBasicCache]);

  // 🗑️ FUNCIONES DE INVALIDACIÓN

  const invalidateQuickMessages = useCallback((userId = null) => {
    // Usar prioritariamente el contexto de autenticación, luego localStorage como fallback
    const currentUserId = userId || user?.id || localStorage.getItem("userId");
    const cacheKey = `${companyId}_${currentUserId}`;
    
    console.log(`🗑️ Invalidando cache de mensajes rápidos para usuario ${currentUserId}`);
    setQuickMessagesCache(prev => {
      const newCache = { ...prev };
      delete newCache[cacheKey];
      return newCache;
    });
    
    try {
      localStorage.removeItem(`${CACHE_KEYS.QUICK_MESSAGES}${cacheKey}`);
      localStorage.removeItem(`${EXPIRY_KEYS.QUICK_MESSAGES}${cacheKey}`);
    } catch (error) {
      console.warn('Error limpiando cache de mensajes rápidos:', error);
    }
  }, [companyId, user?.id]);

  const invalidateUser = useCallback((userId = null) => {
    // Usar prioritariamente el contexto de autenticación, luego localStorage como fallback
    const currentUserId = userId || user?.id || localStorage.getItem("userId");
    
    console.log(`🗑️ Invalidando cache de usuario ${currentUserId}`);
    setUserCache(prev => {
      const newCache = { ...prev };
      delete newCache[currentUserId];
      return newCache;
    });
    
    try {
      localStorage.removeItem(`${CACHE_KEYS.USER}${currentUserId}`);
      localStorage.removeItem(`${EXPIRY_KEYS.USER}${currentUserId}`);
    } catch (error) {
      console.warn('Error limpiando cache de usuario:', error);
    }
  }, [user?.id]);

  const invalidateTicketBasic = useCallback((ticketId) => {
    console.log(`🗑️ Invalidando cache de ticket básico ${ticketId}`);
    setTicketsBasicCache(prev => {
      const newCache = { ...prev };
      delete newCache[ticketId];
      return newCache;
    });
    
    try {
      localStorage.removeItem(`${CACHE_KEYS.TICKETS_BASIC}${ticketId}`);
      localStorage.removeItem(`${EXPIRY_KEYS.TICKETS_BASIC}${ticketId}`);
    } catch (error) {
      console.warn('Error limpiando cache de ticket básico:', error);
    }
  }, []);

  // 🔄 INICIALIZACIÓN: Cargar cache de localStorage al inicio
  useEffect(() => {
    if (!companyId || !user?.id) return;
    
    console.log('🚀 Inicializando cache de recursos estáticos desde localStorage...');
    
    try {
      // Inicializar cache de colas
      const queuesData = getFromLocalStorage('QUEUES', companyId);
      if (queuesData) {
        setQueuesCache(queuesData);
        console.log(`📥 Cache de colas inicializado: ${queuesData?.length || 0} colas`);
      }

      // 🧹 Limpiar caches de usuarios anteriores antes de cargar el actual
      const allQuickMessagesKeys = Object.keys(localStorage).filter(key => 
        key.startsWith(CACHE_KEYS.QUICK_MESSAGES) && key.includes(companyId)
      );
      
      const currentUserCacheKey = `${companyId}_${user.id}`;
      
      allQuickMessagesKeys.forEach(key => {
        const userId = key.replace(CACHE_KEYS.QUICK_MESSAGES, '');
        if (userId !== currentUserCacheKey) {
          // Limpiar cache de usuario anterior
          localStorage.removeItem(key);
          localStorage.removeItem(`${EXPIRY_KEYS.QUICK_MESSAGES}${userId}`);
          console.log(`🗑️ Cache de mensajes rápidos limpiado para usuario anterior ${userId}`);
        }
      });

      // Inicializar cache de mensajes rápidos solo para el usuario actual
      const currentUserData = getFromLocalStorage('QUICK_MESSAGES', currentUserCacheKey);
      if (currentUserData) {
        setQuickMessagesCache({ [currentUserCacheKey]: currentUserData });
        console.log(`📥 Cache de mensajes rápidos inicializado para usuario actual ${currentUserCacheKey}`);
      } else {
        setQuickMessagesCache({}); // Limpiar cualquier cache anterior en memoria
      }

      // 🧹 Limpiar caches de usuarios anteriores para USER cache también
      const allUserKeys = Object.keys(localStorage).filter(key => 
        key.startsWith(CACHE_KEYS.USER)
      );
      
      allUserKeys.forEach(key => {
        const userId = key.replace(CACHE_KEYS.USER, '');
        if (userId !== user.id.toString()) {
          // Limpiar cache de usuario anterior
          localStorage.removeItem(key);
          localStorage.removeItem(`${EXPIRY_KEYS.USER}${userId}`);
          console.log(`🗑️ Cache de usuario limpiado para usuario anterior ${userId}`);
        }
      });

      // Inicializar cache de usuario solo para el usuario actual
      const currentUserInfoData = getFromLocalStorage('USER', user.id);
      if (currentUserInfoData) {
        setUserCache({ [user.id]: currentUserInfoData });
        console.log(`📥 Cache de usuario inicializado para usuario actual ${user.id}`);
      } else {
        setUserCache({}); // Limpiar cualquier cache anterior en memoria
      }

      console.log('✅ Inicialización de cache de recursos estáticos completada');
    } catch (error) {
      console.warn('Error inicializando cache de recursos estáticos:', error);
    }
  }, [companyId, user?.id]);

  // 🔌 LISTENERS DE SOCKET PARA INVALIDACIÓN AUTOMÁTICA
  useEffect(() => {
    if (!companyId) return;
    
    const socket = getSocket(companyId);
    if (!socket) return

    const handleEventQuickMessage = (data) => {
      console.log('🔄 Evento de mensaje rápido recibido:', data.action);
      if (['create', 'update', 'delete'].includes(data.action)) {
        // Invalidar para el usuario específico si se conoce
        invalidateQuickMessages();
        // Recargar automáticamente si es necesario
        setTimeout(() => fetchQuickMessages(null, true), 100);
      }
    }
    const handleEventUser = (data) => {
      if (data.action === "update" && data.user) {
        console.log('🔄 Evento de usuario recibido:', data.action, data.user.id);
        invalidateUser(data.user.id);
        // Recargar automáticamente si es el usuario actual
        if (data.user.id === user?.id) {
          setTimeout(() => fetchUserData(data.user.id, true), 100);
        }
      }
    }

    const handleEventTicket = (data) => {
      if (data.action === "update" && data.ticket) {
        console.log('🔄 Actualizando cache de ticket básico:', data.ticket.id);
        setTicketsBasicCache(prev => ({
          ...prev,
          [data.ticket.id]: data.ticket
        }));
        // También actualizar localStorage
        saveToLocalStorage('TICKETS_BASIC', data.ticket.id, data.ticket, TICKETS_BASIC_CACHE_DURATION);
      }
    }

    // Invalidar cache de mensajes rápidos cuando cambien
    socket.on(`company-${companyId}-quickmessage`, handleEventQuickMessage);

    // Invalidar cache de usuario cuando cambie
    socket.on("user", handleEventUser);

    // Actualizar cache de tickets cuando cambien (sin invalidar, solo actualizar)
    socket.on(`company:ticket`, handleEventTicket);

    return () => {
      socket.off(`company-${companyId}-quickmessage`, handleEventQuickMessage);
      socket.off("user", handleEventUser);
      socket.off(`company:ticket`, handleEventTicket);
    };
  }, [companyId, user?.id]);

  // 🧹 LIMPIEZA AUTOMÁTICA DE CACHE EXPIRADO
  useEffect(() => {
    const cleanupExpiredCache = () => {
      console.log('🧹 Ejecutando limpieza de cache de recursos estáticos...');
      
      try {
        Object.values(CACHE_KEYS).forEach(cacheKeyPrefix => {
          const expiryCacheKeyPrefix = Object.values(EXPIRY_KEYS).find(expiry => 
            expiry.replace('_expiry_', '_') === cacheKeyPrefix
          );
          
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(expiryCacheKeyPrefix)) {
              const itemKey = key.replace(expiryCacheKeyPrefix, '');
              const expiryStr = localStorage.getItem(key);
              if (expiryStr && parseInt(expiryStr) <= Date.now()) {
                console.log(`🗑️ Cache expirado limpiado: ${key}`);
                localStorage.removeItem(key);
                localStorage.removeItem(`${cacheKeyPrefix}${itemKey}`);
              }
            }
          });
        });
        
        console.log('✅ Limpieza de cache de recursos estáticos completada');
      } catch (error) {
        console.warn('Error en limpieza de cache de recursos estáticos:', error);
      }
    };

    // Ejecutar limpieza cada 60 minutos
    const interval = setInterval(cleanupExpiredCache, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <StaticResourcesContext.Provider
      value={{
        // Funciones de fetch con cache
        fetchQueues,
        fetchQuickMessages,
        fetchUserData,
        fetchTicketBasicInfo,
        
        // Estados de cache en memoria (para acceso directo si es necesario)
        queuesCache,
        quickMessagesCache,
        userCache,
        ticketsBasicCache,
        
        // Estados de carga
        loadingStates,
        
        // Funciones de invalidación manual
        invalidateQuickMessages,
        invalidateUser,
        invalidateTicketBasic,
        
        // Función para limpiar todo el cache
        clearAllCache: useCallback(() => {
          invalidateQuickMessages();
          invalidateUser();
          setTicketsBasicCache({});
          console.log('🗑️ Todo el cache de recursos estáticos limpiado');
        }, [invalidateQuickMessages, invalidateUser])
      }}
    >
      {children}
    </StaticResourcesContext.Provider>
  );
};

export { StaticResourcesContext, StaticResourcesContextProvider }; 